/**
 * Body-motion timelines of Bobo, the Happy mascot: the CSS keyframes of the admin `bobo.scss` as plain data.
 *
 * Every motion is a set of tracks sampled at a progress `t` in 0..1 (`ty`/`tx` in SVG units, `sx`/`sy` scale,
 * `rot` degrees, `back` = how much of the back of the head shows, `head` = head tilt in degrees, `eye` = sideways
 * eye shift in SVG units, `arms` = arm wiggle in degrees, `shx`/`sho` = contact-shadow scale / opacity). Between two
 * stops the value is eased with a smoothstep, exactly like a CSS keyframe segment with an ease timing function.
 * Pure data + pure functions, so the timelines are unit-testable and `sample` can run as a reanimated worklet.
 *
 * @module components/bobo/boboMotion
 */

/**
 * Sample a track at progress `t` (smoothstep between stops, clamped at both ends).
 *
 * @param {number[]} stops Ascending stops in 0..1.
 * @param {number[]} values Value at each stop.
 * @param {number} t Progress in 0..1.
 * @returns {number} The eased value.
 */
export function sample(stops, values, t) {
    'worklet';
    const last = stops.length - 1;

    if (t <= stops[0]) {
        return values[0];
    }
    if (t >= stops[last]) {
        return values[last];
    }
    let i = 1;

    while (t > stops[i]) {
        i += 1;
    }
    const p = (t - stops[i - 1]) / (stops[i] - stops[i - 1]);
    const e = p * p * (3 - 2 * p);

    return values[i - 1] + (values[i] - values[i - 1]) * e;
}

const REST = Object.freeze({
    tx: 0, ty: 0, sx: 1, sy: 1, rot: 0, back: 0, head: 0, eye: 0, arms: 0, shx: 1, sho: 1,
});

/**
 * Raw definitions. `ms` is one cycle; `repeat` the number of cycles (-1 = until the motion is replaced);
 * `hold` keeps the last frame (sit, turn, squish); `holdKey` lets two holds with the same end pose hand over
 * without restarting (sit-down → sit). Tracks that are omitted stay at rest.
 */
const RAW = {
    '': { ms: 0 },
    sitDown: {
        ms: 1300, hold: true, holdKey: 'sit', stops: [0, 1],
        ty: [0, 9], sx: [1, 1.07], sy: [1, 0.86], shx: [1, 1.14],
    },
    sit: {
        ms: 600, hold: true, holdKey: 'sit', stops: [0, 1],
        ty: [0, 9], sx: [1, 1.07], sy: [1, 0.86], shx: [1, 1.14],
    },
    standUp: {
        ms: 1300, stops: [0, 0.3, 0.55, 1],
        ty: [9, 5, -6, 0], sx: [1.07, 1.03, 0.95, 1], sy: [0.86, 0.92, 1.1, 1], rot: [0, -2, 1, 0],
        shx: [1.14, 1.06, 0.95, 1],
    },
    turn: {
        ms: 900, hold: true, stops: [0, 0.35, 1],
        sx: [1, 0.05, -1], back: [0, 0, 1],
    },
    jump: {
        ms: 900, repeat: -1, stops: [0, 0.3, 0.5, 0.8, 1],
        ty: [0, -20, -22, 0, 0], sx: [1.08, 0.94, 0.98, 1.1, 1.08], sy: [0.92, 1.08, 1.02, 0.88, 0.92],
        shx: [1.04, 0.72, 0.72, 1.04, 1.04], sho: [1, 0.55, 0.55, 1, 1],
    },
    hop: {
        ms: 750, stops: [0, 0.14, 0.38, 0.55, 0.78, 0.9, 1],
        ty: [0, 3, -26, -30, 0, -3, 0], sx: [1, 1.1, 0.92, 0.97, 1.12, 0.98, 1], sy: [1, 0.88, 1.12, 1.03, 0.86, 1.03, 1],
        shx: [1, 1.06, 0.7, 0.7, 1.1, 1, 1], sho: [1, 1, 0.5, 0.5, 1, 1, 1],
    },
    hopTwice: { ms: 750, repeat: 2, like: 'hop' },
    spin: {
        ms: 1300, stops: [0, 0.1, 0.28, 0.5, 0.72, 0.9, 1],
        ty: [0, 4, -22, -27, -22, 0, 0], sx: [1, 1.08, 0.04, -1, 0.04, 1.1, 1], sy: [1, 0.9, 1.08, 1.05, 1.08, 0.88, 1],
        back: [0, 0, 0.8, 1, 0.8, 0, 0], shx: [1, 1, 0.7, 0.7, 0.7, 1.1, 1], sho: [1, 1, 0.5, 0.5, 0.5, 1, 1],
    },
    dizzy: {
        ms: 2600, stops: [0, 0.12, 0.28, 0.44, 0.6, 0.78, 1],
        rot: [0, -9, 8, -7, 6, -3, 0], tx: [0, -3, 3, -2, 2, 0, 0],
    },
    squish: {
        ms: 160, hold: true, stops: [0, 1],
        ty: [0, 8], sx: [1, 1.12], sy: [1, 0.84], shx: [1, 1.2],
    },
    love: {
        ms: 1400, repeat: -1, stops: [0, 0.15, 0.3, 0.45, 0.6, 0.7, 1],
        sx: [1, 1.06, 1, 1.06, 1, 1, 1], sy: [1, 1.06, 1, 1.06, 1, 1, 1],
    },
    dance: {
        ms: 850, repeat: 4, stops: [0, 0.12, 0.25, 0.5, 0.75, 1],
        tx: [0, 0, -7, 0, 7, 0], ty: [0, 3, -12, 2, -12, 0],
        sx: [1, 1.06, 0.97, 1.06, 0.97, 1], sy: [1, 0.92, 1.04, 0.92, 1.04, 1], rot: [0, -3, -7, 0, 7, 0],
        arms: [-5, 5, -5, 5, -5, 5], head: [-4, 4, -4, 4, -4, 4], shx: [1.04, 1.04, 0.88, 1.04, 0.88, 1.04],
    },
    look: {
        ms: 2800, stops: [0, 0.18, 0.45, 0.58, 0.82, 1],
        tx: [0, -3, -3, 3, 3, 0], rot: [0, -1.5, -1.5, 1.5, 1.5, 0],
        head: [0, -10, -10, 9, 9, 0], eye: [0, -5, -5, 5, 5, 0],
    },
    shake: {
        ms: 450, repeat: 2, stops: [0, 0.25, 0.75, 1],
        tx: [0, -5, 5, 0],
    },
};

/** Every track and its resting value. */
const TRACKS = Object.keys(REST);

/**
 * Fill a raw definition with full tracks, so a worklet never has to check for missing data.
 *
 * @param {string} name Motion name.
 * @param {Object} raw Raw definition (may inherit the tracks of `like`).
 * @returns {Object} The complete definition.
 */
const build = (name, raw) => {
    const source = raw.like ? { ...RAW[raw.like], ...raw } : raw;
    const stops = source.stops || [0, 1];
    const def = {
        name,
        ms: source.ms,
        repeat: source.repeat || 1,
        hold: Boolean(source.hold),
        holdKey: source.holdKey || '',
        stops,
    };

    TRACKS.forEach((track) => {
        def[track] = source[track] || stops.map(() => REST[track]);
    });

    return def;
};

/** Motions indexed by `id` (what the UI-thread worklets read). */
export const MOTION_LIST = Object.freeze(Object.keys(RAW).map((name) => build(name, RAW[name])));

/** Motion definitions by name. */
export const MOTIONS = Object.freeze(MOTION_LIST.reduce((all, def, index) => ({
    ...all,
    [def.name]: {
        ...def,
        index,
    },
}), {}));

/**
 * Evaluate every track of a motion at progress `t`.
 *
 * @param {number} id Motion index in `MOTION_LIST`.
 * @param {number} t Progress in 0..1.
 * @returns {Object} The sampled value of each track (see {@link REST} for the keys).
 */
export function evaluate(id, t) {
    'worklet';
    const m = MOTION_LIST[id];

    return {
        tx: sample(m.stops, m.tx, t),
        ty: sample(m.stops, m.ty, t),
        sx: sample(m.stops, m.sx, t),
        sy: sample(m.stops, m.sy, t),
        rot: sample(m.stops, m.rot, t),
        back: sample(m.stops, m.back, t),
        head: sample(m.stops, m.head, t),
        eye: sample(m.stops, m.eye, t),
        arms: sample(m.stops, m.arms, t),
        shx: sample(m.stops, m.shx, t),
        sho: sample(m.stops, m.sho, t),
    };
}
